import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordLoginRequest } from '@siteday/shared';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';

import { errorMessage, isApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useLogin, useMeQuery } from './session';

type FormValues = z.infer<typeof PasswordLoginRequest>;

/** W1 — email + password for owners/admins; foremen are pointed at the phone app (doc 11). */
export function SignInPage() {
  const me = useMeQuery();
  const login = useLogin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get('from') ?? '/';

  const form = useForm<FormValues>({
    resolver: zodResolver(PasswordLoginRequest),
    defaultValues: { email: '', password: '' },
  });

  if (me.isSuccess) return <Navigate to={from} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      navigate(from, { replace: true });
    } catch (e) {
      form.setError('root', {
        message:
          isApiError(e) && e.status === 401
            ? 'That email and password do not match.'
            : errorMessage(e),
      });
    }
  });

  const companyName = 'SiteDay';
  return (
    <div className="auth-wrap">
      <form
        onSubmit={onSubmit}
        noValidate
        className="w-[380px] max-w-full rounded-2xl border border-line2 bg-surface px-7 pt-7 pb-6 shadow-lg2"
      >
        <div className="flex items-center gap-2.5 font-cond text-[19px] font-bold">
          <span className="relative size-[26px] rounded-[7px] bg-orange">
            <span className="absolute right-1.5 bottom-1.5 left-1.5 h-[5px] rounded-sm bg-ink" />
          </span>
          SiteDay
        </div>
        <h1 className="mt-2 mb-[18px] font-cond text-[22px] font-extrabold">
          Sign in to {companyName}
        </h1>

        <div className="flex flex-col gap-3">
          <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              className="h-10"
              invalid={!!form.formState.errors.email}
              placeholder="you@company.com"
              {...form.register('email')}
            />
          </Field>
          <Field
            label="Password"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              className="h-10"
              invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
          </Field>
          {form.formState.errors.root ? (
            <div
              role="alert"
              className="rounded-lg border border-bad bg-hi-bad px-3 py-2 text-xs font-semibold text-bad"
            >
              {form.formState.errors.root.message}
            </div>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="mt-1.5 w-full"
            loading={login.isPending}
          >
            Sign in
          </Button>
        </div>

        <a
          href="mailto:support@fuorix.example?subject=SiteDay%20password%20reset"
          className="mt-3.5 block text-center text-xs font-semibold text-orange-ink"
        >
          Forgot your password?
        </a>
        <p className="mt-[18px] text-center text-xs text-ink3">
          Foremen: install the SiteDay app and sign in with your phone number.
        </p>
        {import.meta.env.VITE_MOCK === '1' ? (
          <p className="mt-3 rounded-md bg-bg2 px-2.5 py-1.5 text-center text-[11px] text-ink3">
            Mock mode · try <b className="text-ink2">marissa@ridgeline.example</b> /{' '}
            <b className="text-ink2">Password1!</b>
          </p>
        ) : null}
      </form>
    </div>
  );
}
